import { useState, useMemo } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useMutation } from "@apollo/client";
import { showSnackbar } from "@/lib/snackbarUtils";
import { getBackendNavigationOrigin } from "@/lib/backendSource";
import { isDev } from "@/lib/urlUtils";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import Select from "@/components/Select";
import TextField from "@/components/TextField";
import Banner from "@/components/Banner";
import { UPDATE_COHORT_ENROLLMENT_MUTATION } from "./mutations";
import type { UpdateCohortEnrollmentInput, UpdateCohortEnrollmentResponse } from "./types";

type CohortOption = {
  id: string;
  name: string;
};

type EditCohortFormValues = {
  newCohortId: string;
  noteContent: string;
};

type EditCohortDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  enrollmentId: string;
  selectedStudent: {
    displayName: string;
    currentCohort: CohortOption | null;
  };
  cohorts: CohortOption[];
};

const SEASON_ORDER: Record<string, number> = {
  Spring: 1,
  Summer: 2,
  Fall: 3
};

function cohortSortRank(name: string): number {
  const parts = name.split(" ");
  const season = parts[0];
  const year = parseInt(parts[1], 10) || 0;
  const seasonRank = SEASON_ORDER[season] ?? 0;
  return year * 10 + seasonRank;
}

function deriveStatusLabel(oldCohortName: string, newCohortName: string): string {
  const oldRank = cohortSortRank(oldCohortName);
  const newRank = cohortSortRank(newCohortName);

  if (newRank > oldRank) {
    return "behind cohort";
  }
  return "ahead of cohort";
}

const RAILS_ADMIN_COHORT_PATH = "/pga_admin/education~university_cohort";

function getRailsAdminCohortUrl(): string {
  if (!isDev()) {
    return `https://coach-tools.pga.com${RAILS_ADMIN_COHORT_PATH}`;
  }

  return `${getBackendNavigationOrigin()}${RAILS_ADMIN_COHORT_PATH}`;
}

export default function EditCohortDialog({
  isOpen,
  onClose,
  onSuccess,
  enrollmentId,
  selectedStudent,
  cohorts
}: EditCohortDialogProps) {
  const [error, setError] = useState<string | null>(null);

  const formMethods = useForm<EditCohortFormValues>({
    defaultValues: {
      newCohortId: "",
      noteContent: ""
    }
  });

  const { handleSubmit, control, watch, reset } = formMethods;

  const selectedNewCohortId = watch("newCohortId");
  const noteContent = watch("noteContent");

  const cohortOptions = useMemo(() => {
    return cohorts
      .filter((c) => c.id !== selectedStudent.currentCohort?.id)
      .sort((a, b) => cohortSortRank(a.name) - cohortSortRank(b.name))
      .map((c) => ({
        value: c.id,
        label: c.name
      }));
  }, [cohorts, selectedStudent.currentCohort?.id]);

  const statusIndicator = useMemo(() => {
    if (!selectedNewCohortId || !selectedStudent.currentCohort) return null;
    const newCohort = cohorts.find((c) => c.id === selectedNewCohortId);
    if (!newCohort) return null;
    const statusLabel = deriveStatusLabel(selectedStudent.currentCohort.name, newCohort.name);
    return `Student will be marked as "${statusLabel}" on their ${selectedStudent.currentCohort.name} enrollment.`;
  }, [selectedNewCohortId, selectedStudent.currentCohort, cohorts]);

  const [updateCohortEnrollment, { loading: mutationLoading }] = useMutation<
    UpdateCohortEnrollmentResponse,
    { input: UpdateCohortEnrollmentInput }
  >(UPDATE_COHORT_ENROLLMENT_MUTATION);

  const onSubmit = async (formData: EditCohortFormValues) => {
    setError(null);
    try {
      const result = await updateCohortEnrollment({
        variables: {
          input: {
            enrollmentId,
            newCohortId: formData.newCohortId,
            noteContent: formData.noteContent
          }
        }
      });

      if (result.data?.updateCohortEnrollment?.success) {
        const newCohortName =
          result.data.updateCohortEnrollment.cohortEnrollment?.universityCohort.name ?? "new cohort";
        showSnackbar(`Student moved to ${newCohortName}`);
        reset();
        onSuccess();
        onClose();
      } else {
        setError(result.data?.updateCohortEnrollment?.message || "Failed to update cohort enrollment.");
      }
    } catch (err: any) {
      if (err?.graphQLErrors?.length > 0) {
        setError(err.graphQLErrors[0].message || "An error occurred");
      } else if (err?.networkError) {
        setError("Network error occurred. Please try again.");
      } else {
        setError("Failed to update cohort enrollment.");
      }
    }
  };

  const handleClose = () => {
    reset();
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  const canSubmit = selectedNewCohortId && noteContent.trim().length > 0 && !mutationLoading;

  return (
    <Dialog
      open={isOpen}
      data-testid="edit-cohort-dialog"
      onClosed={handleClose}
      hasFormElements
      displayOverflow
      headline="Edit Student Cohort"
      className="w-[28rem]"
      actions={
        <div className="flex gap-2">
          <Button variant="filledTonal" onClick={handleClose}>
            Cancel
          </Button>
          {cohortOptions.length > 0 && (
            <Button variant="filled" onClick={handleSubmit(onSubmit)} disabled={!canSubmit}>
              Save Changes
            </Button>
          )}
        </div>
      }
    >
      <FormProvider {...formMethods}>
        <div className="flex flex-col gap-6">
          {error && (
            <Banner variant="error" description={error} inline={true} closable={false} hideSecondaryDismiss={true} />
          )}

          <div className="text-body-medium">
            <strong>Student:</strong> {selectedStudent.displayName}
          </div>

          <div className="text-body-medium">
            <strong>Current Cohort:</strong> {selectedStudent.currentCohort?.name ?? "None"}
          </div>

          {cohortOptions.length === 0 ? (
            <div className="flex flex-col gap-3">
              <Banner
                variant="info"
                description="No other cohorts are available for this university."
                inline
                closable={false}
                hideSecondaryDismiss
              />
              <Button variant="outlined" onClick={() => window.open(getRailsAdminCohortUrl(), "_blank")}>
                Add Cohort in Rails Admin
              </Button>
            </div>
          ) : (
            <Controller
              name="newCohortId"
              control={control}
              rules={{ required: "New cohort is required" }}
              render={({ field, fieldState }) => (
                <Select
                  label="New Cohort"
                  required
                  options={cohortOptions}
                  value={field.value}
                  onSelect={(value) => field.onChange(value)}
                  error={fieldState.invalid}
                  errorText={fieldState.error?.message}
                  data-testid="new-cohort-select"
                />
              )}
            />
          )}

          {cohortOptions.length > 0 && (
            <>
              {statusIndicator && (
                <Banner
                  variant="info"
                  description={statusIndicator}
                  inline={true}
                  closable={false}
                  hideSecondaryDismiss
                />
              )}

              <Controller
                name="noteContent"
                control={control}
                rules={{ required: "Reason for change is required" }}
                render={({ field, fieldState }) => (
                  <TextField
                    label="Reason for Change *"
                    value={field.value}
                    onChange={field.onChange}
                    error={fieldState.invalid}
                    errorText={fieldState.error?.message}
                    type="textarea"
                    rows={4}
                    className="w-full"
                    placeholder="Explain why this student is being moved to a different cohort"
                  />
                )}
              />
            </>
          )}
        </div>
      </FormProvider>
    </Dialog>
  );
}
